import React from 'react';
import {
  Paper, Box, Button, Typography, CircularProgress, List, ListItem, Chip, Pagination
} from '@mui/material';
import SortIcon from '@mui/icons-material/Sort';

const formatDisplayDate = (dateString) => {
    if (!dateString || dateString === 'N/A') return 'N/A';
    try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        }).format(date);
    } catch {
        return dateString;
    }
};

const RecordListPanel = ({
  isLoading,
  error,
  paginatedRecords,
  selectedRecordId,
  setSelectedRecordId,
  allFilteredRecords,
  toggleSortDirection,
  sortKey,
  sortDirection,
  pageCount,
  currentPage,
  handlePageChange,
  selectedYear,
}) => {
  return (
    <Paper
      elevation={3}
      sx={{
        height: '800px',
        display: 'flex',
        flexDirection: 'column',
        p: 0,
        width: '300px',
      }}
      id="record-list-container"
    >
      {/* --- 1. STATIC TOP: Sort Controls --- */}
      <Box
        sx={{
          p: 1,
          borderBottom: '1px solid #ccc',
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          flexShrink: 0,
          flexWrap: 'wrap', // Added to handle the third button
        }}
      >
        <Box>
          <Button
            onClick={() => toggleSortDirection('date')}
            startIcon={<SortIcon />}
            size="small"
            variant={sortKey === 'date' ? 'contained' : 'outlined'}
          >
            Date{' '}
            {sortKey === 'date' && (sortDirection === 'asc' ? '▲' : '▼')}
          </Button>
          <Button
            onClick={() => toggleSortDirection('status')}
            startIcon={<SortIcon />}
            size="small"
            variant={sortKey === 'status' ? 'contained' : 'outlined'}
            sx={{ ml: 1 }}
          >
            Status{' '}
            {sortKey === 'status' && (sortDirection === 'asc' ? '▲' : '▼')}
          </Button>
          <Button
              onClick={() => toggleSortDirection('documentCount')}
              startIcon={<SortIcon />}
              size="small"
              variant={sortKey === 'documentCount' ? 'contained' : 'outlined'}
              sx={{ ml: 1 }}
          >
              Docs{' '}
              {sortKey === 'documentCount' && (sortDirection === 'asc' ? '▲' : '▼')}
          </Button>
        </Box>
      </Box>

      {/* --- 2. SCROLLING CONTENT: List, Loading, Errors --- */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>

        {error && (
          <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
            Error: {error}
          </Typography>
        )}

        {!isLoading && !error && paginatedRecords.length > 0 && (
          <List dense id="record-list">
            {paginatedRecords.map((record) => (
              <ListItem
                key={record.id}
                button
                onClick={() => setSelectedRecordId(record.id)}
                selected={record.id === selectedRecordId}
                sx={{
                  borderBottom: '1px solid #eee',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '8px 16px',
                  position: 'relative',
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)', // Example: a light blue (primary color with opacity)
                    
                    // Crucially, override the hover effect *when selected*
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.18)', // Slightly darker on hover
                    },
                  },
                  
                  // Ensure standard hover works when NOT selected (optional, but good practice)
                  '&:hover': {
                      backgroundColor: '#f5f5f5', // Light gray hover for unselected items
                  },
                }}
              >
                {/* --- TOP LINE: Record {id} --- */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 0.5,
                    }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Record {record.id} {/* Changed to include "Record" prefix */}
                  </Typography>
                </Box>
                
                {/* --- BOTTOM LINE: Date | Status Pill | Docs Pill --- */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {/* Bottom Line Left: Date */}
                    <Typography variant="caption" color="textSecondary" sx={{ flexGrow: 1, textAlign: 'left' }}>
                        {formatDisplayDate(record.date)} {/* Use helper function for formatting */}
                    </Typography>

                    {/* Bottom Line Middle: Status Pill */}
                    <Chip
                        label={record.status}
                        size="small"
                        color={record.status === 'Open' ? 'success' : 'error'}
                        sx={{ ml: 1, mr: 1 }} // Add margin for spacing
                    />

                    {/* Bottom Line Right: Docs Pill */}
                    <Chip
                        label={`${record.documentCount} Docs`}
                        size="small"
                        color={record.documentCount > 0 ? 'primary' : 'default'}
                        variant="outlined"
                    />
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        {!isLoading && !error && allFilteredRecords.length === 0 && (
          <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
            No records match the current filters or the selected year's data is
            empty.
          </Typography>
        )}
      </Box>

      {/* --- 3. STATIC BOTTOM: Pagination Controls --- */}
      {pageCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1,
            borderTop: '1px solid #ccc',
            flexShrink: 0,
          }}
        >
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Paper>
  );
};

export default RecordListPanel;